import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, CreditCard, Search, Unlock, PhoneCall, Settings, X, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useOverlayLock } from '@/lib/overlay';
import { markTourSeen } from '@/lib/activation';
import { LEAD_PRICE, money, planLabel } from '@/data/constants';

/**
 * FIRST-BUYER WALKTHROUGH.
 *
 * Five screens, one idea each, in plain language a child could follow:
 *   1. Payment successful      2. Credits received     3. Pick a project
 *   4. Unlock a lead           5. Contact the customer
 *
 * The tour also answers the "where is everything" questions in step copy:
 * the balance lives on the dashboard card, purchased leads live under
 * "My Leads", and the membership is managed from the dashboard.
 *
 * Behaviour: opens once automatically after the first purchase, can be skipped
 * at any point (skipping counts as seen), and can be replayed forever from the
 * dashboard. Copy is bilingual and kept HERE, in one place, so the two
 * languages cannot drift apart.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Plan that was just purchased — used in step 1/2 copy. */
  plan?: string;
  /** Credits the buyer now holds. */
  credits?: number;
  /** Where "Browse projects" should go. */
  jobsHref: string;
}

interface Step {
  icon: React.ReactNode;
  titleEn: string;
  titleEs: string;
  bodyEn: string;
  bodyEs: string;
}

const OnboardingTour: React.FC<Props> = ({ open, onClose, plan, credits = 0, jobsHref }) => {
  const { lang } = useI18n();
  const [i, setI] = useState(0);
  useOverlayLock(open);

  useEffect(() => {
    if (open) setI(0);
  }, [open]);

  if (!open) return null;

  const planName = plan ? planLabel(plan, lang) : lang === 'es' ? 'Membresía' : 'Membership';
  const n = credits > 0 ? credits : 1;

  const steps: Step[] = [
    {
      icon: <CheckCircle2 className="h-10 w-10 text-emerald-600" />,
      titleEn: 'Payment successful',
      titleEs: 'Pago exitoso',
      bodyEn: `Your ${planName} is now active. Nothing else to do — you are all set.`,
      bodyEs: `Su ${planName} ya está activa. No hay nada más que hacer — todo está listo.`,
    },
    {
      icon: <CreditCard className="h-10 w-10 text-orange-500" />,
      titleEn: 'Credits received',
      titleEs: 'Créditos recibidos',
      bodyEn: `You now have ${n} lead ${n === 1 ? 'credit' : 'credits'} available. Your balance is always shown at the top of your dashboard and on every project page.`,
      bodyEs: `Ahora tiene ${n} ${n === 1 ? 'crédito' : 'créditos'} de leads. Su saldo aparece siempre arriba en su panel y en cada página de proyecto.`,
    },
    {
      icon: <Search className="h-10 w-10 text-blue-600" />,
      titleEn: 'Pick a project',
      titleEs: 'Elija un proyecto',
      bodyEn: 'Open Projects and choose a job you would like to work on. Filter by trade, state and budget.',
      bodyEs: 'Abra Proyectos y elija un trabajo que quiera hacer. Filtre por oficio, estado y presupuesto.',
    },
    {
      icon: <Unlock className="h-10 w-10 text-slate-900" />,
      titleEn: 'Unlock a lead',
      titleEs: 'Desbloquee un lead',
      bodyEn: `Tap "Unlock Lead". 1 credit = 1 lead. No credits left? One lead costs ${money(LEAD_PRICE)}. Your remaining balance updates instantly, and every lead you unlock is saved under "My Leads".`,
      bodyEs: `Toque "Desbloquear Lead". 1 crédito = 1 lead. ¿Sin créditos? Un lead cuesta ${money(LEAD_PRICE)}. Su saldo se actualiza al instante y cada lead que desbloquea se guarda en "Mis Leads".`,
    },
    {
      icon: <PhoneCall className="h-10 w-10 text-emerald-600" />,
      titleEn: 'Contact the customer',
      titleEs: 'Contacte al cliente',
      bodyEn: 'You immediately see the phone number, email, address and full project details. Call or text right away — the first contractor to call usually wins the job. Manage your membership any time from your dashboard.',
      bodyEs: 'Verá de inmediato el teléfono, correo, dirección y todos los detalles del proyecto. Llame o escriba de una vez — el primer contratista que llama suele ganar el trabajo. Administre su membresía cuando quiera desde su panel.',
    },
  ];

  const step = steps[i];
  const last = i === steps.length - 1;

  const finish = () => {
    markTourSeen();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-slate-900/70 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {lang === 'es' ? `Paso ${i + 1} de ${steps.length}` : `Step ${i + 1} of ${steps.length}`}
          </p>
          <button
            type="button"
            onClick={finish}
            style={{ touchAction: 'manipulation' }}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            {lang === 'es' ? 'Saltar' : 'Skip'} <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="px-6 py-8 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-50">
            {step.icon}
          </div>
          <h2 className="mt-5 text-2xl font-extrabold text-slate-900">
            {lang === 'es' ? step.titleEs : step.titleEn}
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-base leading-relaxed text-slate-600">
            {lang === 'es' ? step.bodyEs : step.bodyEn}
          </p>
        </div>

        <div className="flex justify-center gap-2 pb-5">
          {steps.map((_s, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`${idx + 1}`}
              onClick={() => setI(idx)}
              className={`h-2.5 rounded-full transition-all ${
                idx === i ? 'w-7 bg-orange-500' : 'w-2.5 bg-slate-200'
              }`}
            />
          ))}
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-5 sm:flex-row">
          {i > 0 && (
            <button
              type="button"
              onClick={() => setI((v) => v - 1)}
              style={{ touchAction: 'manipulation' }}
              className="min-h-[52px] flex-1 rounded-xl border-2 border-slate-300 px-4 text-base font-bold text-slate-700 hover:bg-slate-50"
            >
              {lang === 'es' ? 'Atrás' : 'Back'}
            </button>
          )}
          {last ? (
            <Link
              to={jobsHref}
              onClick={finish}
              style={{ touchAction: 'manipulation' }}
              className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 text-base font-bold text-white hover:bg-orange-600"
            >
              {lang === 'es' ? 'Ver Proyectos' : 'Browse Projects'} <ArrowRight className="h-5 w-5" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setI((v) => v + 1)}
              style={{ touchAction: 'manipulation' }}
              className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-base font-bold text-white hover:bg-slate-800"
            >
              {lang === 'es' ? 'Siguiente' : 'Next'} <ArrowRight className="h-5 w-5" />
            </button>
          )}
        </div>

        <p className="flex items-center justify-center gap-1.5 pb-5 text-[11px] font-semibold text-slate-400">
          <Settings className="h-3 w-3" />
          {lang === 'es'
            ? 'Puede volver a ver esta guía desde su panel.'
            : 'You can replay this guide any time from your dashboard.'}
        </p>
      </div>
    </div>
  );
};

export default OnboardingTour;
